import { BorderTable } from '@/components/BorderTable';
import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  SKELETON_GAP_XS,
  SKELETON_HEIGHT_XS,
} from '@/components/SkeletonLoading/constants';
import {
  TransactionItemOptionsQuery,
  useGetTransactionItemDetailOptions,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { TransactionDetailOptionButtonGroup } from '@/features/transaction_items/Detail/components/ButtonGroup/TransactionDetailOptionButtonGroup';
import { useDeleteTransactionDetailOption } from '@/features/transaction_items/Detail/hooks/useDeleteTransactionDetailOption';
import { useTransactionItemDetailOptionsColumns } from '@/features/transaction_items/Detail/Table/Column/column';
import {
  TransactionDetailOptionFormType,
  TransactionItemDetailOptionsColumn,
} from '@/features/transaction_items/Detail/type';
import { useTranslation } from '@/i18n';
import {
  Loading,
  ModernPaginationProps,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export type TransactionItemsDetailOptionsTableViewProps = {
  searchQuery: TransactionItemOptionsQuery;
  onSearch: (query: TransactionItemOptionsQuery) => void;
  tableBodyHeight: number;
  setDetailTableLoading: Dispatch<SetStateAction<boolean>>;
  isEdit?: boolean;
  onEdit: (id: string, data: TransactionDetailOptionFormType) => void;
  onAdd: () => void;
  onImport: () => void;
};

type props = {
  pagination: ModernPaginationProps;
  table: TransactionItemDetailOptionProps;
  onEdit: (id: string, data: TransactionDetailOptionFormType) => void;
};

export type TransactionItemDetailOptionProps = Omit<
  TableProps<TransactionItemDetailOptionsColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

export const TransactionItemDetailTable: FC<props> = memo(
  ({ table, pagination, onEdit }) => {
    const { onDelete: _onDelete, isLoading } =
      useDeleteTransactionDetailOption();
    const onDelete = useCallback(
      (id: string, label: string) => {
        _onDelete(id, label);
      },
      [_onDelete]
    );

    const { columns, DeleteConfirmModal } =
      useTransactionItemDetailOptionsColumns({
        onDelete,
        onEdit,
      });

    return (
      <div className={cx(styles['pulldown-option-table'])}>
        <BorderTable
          tableLayout='auto'
          {...table}
          columns={columns}
          legacy={false}
          pagination={pagination}
          hideHeaderPagination
          disableRowStyling
        />
        <DeleteConfirmModal />
        {isLoading && (
          <Loading size='large' color='white' isFullFlame isOverlay />
        )}
      </div>
    );
  }
);
TransactionItemDetailTable.displayName = 'TransactionItemDetailTable';

export const InnerTable: FC<TransactionItemsDetailOptionsTableViewProps> = memo(
  ({
    searchQuery: _searchQuery,
    onSearch,
    tableBodyHeight,
    setDetailTableLoading,
    isEdit = false,
    onEdit,
    onAdd,
    onImport,
  }) => {
    const { transaction_item_id } = useParams();

    const { t } = useTranslation();

    const { data, pagination: _pagination } =
      useGetTransactionItemDetailOptions(
        transaction_item_id ?? '',
        _searchQuery
      );

    const onPageChange = useCallback(
      (page: number) => {
        onSearch({ page });
      },
      [onSearch]
    );

    const tbodyStyle = useMemo(() => {
      return {
        height: tableBodyHeight,
      };
    }, [tableBodyHeight]);

    const table: TransactionItemDetailOptionProps = useMemo(() => {
      return {
        data: data ?? [],
        scroll: data.length > 0 ? { y: tableBodyHeight } : undefined,
        components:
          data.length === 0
            ? {
                body: {
                  wrapper: ({ children }: { children: React.ReactNode }) => (
                    <tbody style={tbodyStyle}>{children}</tbody>
                  ),
                },
              }
            : undefined,
        emptyText: <div>{t('transaction_items.detail.options.empty')}</div>,
        tableAction: (
          <TransactionDetailOptionButtonGroup
            onAdd={onAdd}
            onImport={onImport}
            isDisabled={isEdit}
          />
        ),
      };
    }, [data, tableBodyHeight, tbodyStyle, t, onAdd, isEdit, onImport]);

    const pagination: ModernPaginationProps = useMemo(() => {
      return {
        ..._pagination,
        onChange: onPageChange,
        currentPage: _searchQuery.page ?? 1,
        showPerPage: true,
        currentPerPage: 25,
      };
    }, [_pagination, onPageChange, _searchQuery.page]);

    useEffect(() => {
      setDetailTableLoading(false);
    }, [setDetailTableLoading, _pagination, _searchQuery]);

    return (
      <TransactionItemDetailTable
        table={table}
        pagination={pagination}
        onEdit={onEdit}
      />
    );
  }
);
InnerTable.displayName = 'InnerTable';

export const FallbackTable: FC<{
  emptyType: 'loading' | 'error';
  tableBodyHeight: number;
  setDetailTableLoading: Dispatch<SetStateAction<boolean>>;
  onEdit: (id: string, data: TransactionDetailOptionFormType) => void;
}> = memo(({ tableBodyHeight, emptyType, setDetailTableLoading, onEdit }) => {
  const repeatCount = useMemo(
    () =>
      Math.floor(
        (tableBodyHeight - 24) / (SKELETON_HEIGHT_XS + SKELETON_GAP_XS)
      ),
    [tableBodyHeight]
  );

  const tbodyStyle = useMemo(() => {
    return {
      height: tableBodyHeight,
    };
  }, [tableBodyHeight]);

  const tableProps: Omit<TransactionItemDetailOptionProps, 'scroll'> =
    useMemo(() => {
      return {
        data: [],
        emptyText:
          emptyType === 'loading' ? (
            <LoadingSkeletonList repeatCount={repeatCount} />
          ) : (
            <></>
          ),
        emptyType,
        legacy: false,
        hideFooterPagination: true,
        hideHeaderPagination: true,
        tableAction: (
          <TransactionDetailOptionButtonGroup
            onAdd={() => {}}
            onImport={() => {}}
            isDisabled
          />
        ),
        components:
          emptyType === 'error'
            ? {
                body: {
                  wrapper: ({ children }: { children: React.ReactNode }) => (
                    <tbody style={tbodyStyle}>{children}</tbody>
                  ),
                },
              }
            : undefined,
      };
    }, [emptyType, repeatCount, tbodyStyle]);

  const pagination: ModernPaginationProps = useMemo(() => {
    return {
      currentPage: 1,
      maxPage: 1,
      mode: 'label',
    };
  }, []);

  useEffect(() => {
    setDetailTableLoading(true);
  }, [setDetailTableLoading]);

  return (
    <TransactionItemDetailTable
      table={tableProps}
      pagination={pagination}
      onEdit={onEdit}
    />
  );
});
FallbackTable.displayName = 'FallbackTable';

export const TransactionItemsDetailOptionsTableView: FC<TransactionItemsDetailOptionsTableViewProps> =
  memo((props) => {
    return (
      <ErrorBoundary fallback={<FallbackTable {...props} emptyType='error' />}>
        <Suspense fallback={<FallbackTable {...props} emptyType='loading' />}>
          <InnerTable {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  });
TransactionItemsDetailOptionsTableView.displayName =
  'TransactionItemsDetailOptionsTable';
